<template>
    <div class="manual-step flex flex-col h-full">
        <!-- MISSING CARD REQUEST STEP (step 0) -->
        <template v-if="currentStep === 0">
            <!-- Main Container -->
            <div
                class="flex w-full p-6 flex-col items-center gap-8 rounded-none md:rounded-lg bg-white dark:bg-slate-800 shadow-none md:shadow-[0_1px_3px_0_rgba(0,0,0,0.10),0_1px_2px_0_rgba(0,0,0,0.06)] pb-24 md:pb-6 flex-1 min-h-0 overflow-y-auto"
            >
                <!-- Header Container -->
                <div class="flex items-start justify-between self-stretch">
                    <div class="flex items-center gap-4">
                        <!-- Pill with Icon -->
                        <div
                            class="flex w-12 h-12 p-3.5 flex-col justify-center items-center gap-2.5 rounded-full bg-slate-100 dark:bg-slate-700"
                        >
                            <Icon
                                icon="unknown-card"
                                class="w-5 h-5 text-slate-600 dark:text-slate-300"
                            />
                        </div>

                        <!-- Title -->
                        <h2
                            class="text-slate-800 dark:text-slate-100 text-xl font-medium leading-7"
                        >
                            Missing card request
                        </h2>
                    </div>
                    <button
                        type="button"
                        @click="$emit('closeMissingCard')"
                        class="btn-icon btn-transparent w-10"
                    >
                        <Icon icon="close" />
                    </button>
                </div>

                <!-- Description -->
                <div class="self-stretch">
                    <p class="text-slate-800 dark:text-slate-100 text-sm font-normal leading-5">
                        Please provide accurate card details about your
                        card, so we can add the correct one to our system.
                        We'll notify you once it's available.
                    </p>
                </div>

                <!-- Form Container -->
                <div
                    class="flex flex-col items-start gap-6 self-stretch rounded-lg"
                >
                    <!-- Issuing Bank -->
                    <div class="flex flex-col items-start self-stretch">
                        <label
                            class="text-slate-600 dark:text-slate-300 text-sm font-medium leading-5 mb-2"
                        >
                            Issuing bank
                        </label>
                        <div
                            class="flex py-0.5 px-1 items-center gap-2 self-stretch rounded border bg-white dark:bg-slate-800 transition-colors"
                            :class="
                                fieldErrors.card_not_found_bank
                                    ? 'border-red-500'
                                    : 'border-slate-200 dark:border-slate-700 focus-within:border-slate-800 dark:focus-within:border-slate-500 hover:border-slate-800 dark:hover:border-slate-500'
                            "
                        >
                            <input
                                type="text"
                                v-model="manualCard.card_not_found_bank"
                                placeholder="Enter issuing bank"
                                class="flex-1 text-slate-800 dark:text-slate-100 text-sm font-normal leading-5 bg-transparent border-none outline-none focus:ring-0 placeholder:text-slate-500 dark:placeholder-slate-500"
                                @focus="
                                    $emit('clearFieldError', 'card_not_found_bank')
                                "
                            />
                        </div>
                        <!-- Error message -->
                        <transition name="fade">
                            <div
                                class="text-red-500 text-xs flex items-center gap-2 py-1.5"
                                role="alert"
                                v-if="fieldErrors.card_not_found_bank"
                            >
                                <Icon icon="caution" />
                                {{ fieldErrors.card_not_found_bank }}
                            </div>
                        </transition>
                    </div>

                    <!-- Card Name -->
                    <div class="flex flex-col items-start self-stretch">
                        <label
                            class="text-slate-600 dark:text-slate-300 text-sm font-medium leading-5 mb-2"
                        >
                            Card name
                        </label>
                        <div
                            class="flex py-0.5 px-1 items-center gap-2 self-stretch rounded border bg-white dark:bg-slate-800 transition-colors"
                            :class="
                                fieldErrors.card_not_found_name
                                    ? 'border-red-500'
                                    : 'border-slate-200 dark:border-slate-700 focus-within:border-slate-800 dark:focus-within:border-slate-500 hover:border-slate-800 dark:hover:border-slate-500'
                            "
                        >
                            <input
                                type="text"
                                v-model="manualCard.card_not_found_name"
                                placeholder="Enter card name"
                                class="flex-1 text-slate-800 dark:text-slate-100 text-sm font-normal leading-5 bg-transparent border-none outline-none focus:ring-0 placeholder:text-slate-500 dark:placeholder-slate-500"
                                @focus="
                                    $emit('clearFieldError', 'card_not_found_name')
                                "
                            />
                        </div>
                        <!-- Error message -->
                        <transition name="fade">
                            <div
                                class="text-red-500 text-xs flex items-center gap-2 py-1.5"
                                role="alert"
                                v-if="fieldErrors.card_not_found_name"
                            >
                                <Icon icon="caution" />
                                {{ fieldErrors.card_not_found_name }}
                            </div>
                        </transition>
                    </div>
                </div>
            </div>

            <!-- Mobile bottom bar: Back / Send request -->
            <div
                class="md:hidden fixed bottom-12 left-0 right-0 bg-white dark:bg-slate-800 px-4 pt-3 pb-[max(12px,env(safe-area-inset-bottom))] z-[1000]"
            >
                <div class="grid grid-cols-2 gap-2">
                    <button
                        type="button"
                        @click="$emit('update:currentStep', 1)"
                        class="w-full py-3 rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-100 text-sm font-medium leading-5 hover:bg-slate-50 dark:hover:bg-slate-700"
                    >
                        Back
                    </button>
                    <button
                        type="button"
                        @click="$emit('submitMissingCard')"
                        class="w-full py-3 rounded-md bg-slate-800 dark:bg-slate-600 text-white text-sm font-medium leading-5 hover:bg-slate-700 dark:hover:bg-slate-500"
                    >
                        Send request
                    </button>
                </div>
            </div>

            <!-- Footer (desktop only) -->
            <div
                class="hidden md:flex justify-between items-center p-6 bg-white dark:bg-slate-800 flex-shrink-0 mt-auto w-full"
            >
                <!-- Back Button -->
                <button
                    type="button"
                    @click="$emit('update:currentStep', 1)"
                    class="flex py-2.5 px-4 items-start gap-2 rounded border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 shadow-[0_1px_2px_0_rgba(0,0,0,0.05)] text-slate-800 dark:text-slate-100 text-sm font-medium leading-5 hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors"
                >
                    Back
                </button>

                <!-- Right Buttons -->
                <div class="flex gap-2">
                    <!-- Cancel Button -->
                    <button
                        type="button"
                        @click="$emit('gotoStep', 1)"
                        class="flex py-2.5 px-4 items-start gap-2 rounded border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 shadow-[0_1px_2px_0_rgba(0,0,0,0.05)] text-slate-800 dark:text-slate-100 text-sm font-medium leading-5 hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors"
                    >
                        Cancel
                    </button>

                    <!-- Send Request Button -->
                    <button
                        type="button"
                        @click="$emit('submitMissingCard')"
                        class="flex py-2.5 px-4 justify-center items-center gap-2 rounded bg-slate-800 dark:bg-slate-600 shadow-[0_1px_2px_0_rgba(0,0,0,0.05)] text-white text-sm font-medium leading-5 hover:bg-slate-700 dark:hover:bg-slate-500 transition-colors"
                    >
                        Send request
                    </button>
                </div>
            </div>
        </template>

        <!-- CARD SEARCH STEP (step 1) -->
        <template v-else>
            <!-- Main Container -->
            <div
                class="flex w-full p-6 flex-col items-center gap-8 rounded-lg bg-white dark:bg-slate-800 pb-24 md:pb-6 flex-1 min-h-0 overflow-y-auto"
            >
                <!-- Header Container -->
                <div class="flex items-start justify-between self-stretch">
                    <div
                        class="flex flex-col justify-center items-start gap-1 flex-1"
                    >
                        <!-- Title -->
                        <h2
                            class="text-slate-800 dark:text-slate-100 text-xl font-medium leading-7"
                        >
                            Add card
                        </h2>
                        <!-- Subtitle -->
                        <p
                            class="text-slate-500 dark:text-slate-400 text-sm font-normal leading-5"
                        >
                            {{
                                hasMultipleUsers && user && user.name
                                    ? user.name
                                    : 'Select card product'
                            }}
                        </p>
                    </div>
                    <button
                        type="button"
                        @click="$emit('close')"
                        class="btn-icon btn-transparent w-10"
                    >
                        <Icon icon="close" />
                    </button>
                </div>

                <!-- Mobile step lines -->
                <div
                    class="flex md:hidden justify-center items-center gap-2 self-stretch"
                >
                    <div class="flex-1 h-1 rounded bg-slate-800 dark:bg-slate-100"></div>
                    <div class="flex-1 h-1 rounded bg-slate-200 dark:bg-slate-600"></div>
                </div>

                <!-- Card Visual Container -->
                <div
                    class="flex w-full max-w-[296px] h-[184px] justify-center items-center rounded-[12px] bg-slate-100 dark:bg-slate-700 border border-slate-200 dark:border-slate-600"
                >
                    <!-- Card Image or Fallback -->
                    <div
                        v-if="
                            manualChosenCard && manualChosenCard.card_image
                        "
                        class="w-full h-full"
                    >
                        <img
                            :src="`${cdnUrl}/${manualChosenCard.card_image?.path}/_medium/${manualChosenCard.card_image?.file_name}`"
                            :alt="manualChosenCard.title"
                            class="w-full h-full object-cover rounded-[12px]"
                        />
                    </div>
                    <!-- Fallback Card Design -->
                    <div v-else class="w-full h-full">
                        <img
                            src="/images/cards/card-wallet-manually.png"
                            alt="Select a card"
                            class="w-full h-full object-cover rounded-[12px]"
                        />
                    </div>
                </div>

                <!-- Form Container -->
                <div class="flex flex-col items-start self-stretch">
                    <!-- Card Selection -->
                    <div class="flex flex-col items-start self-stretch">
                        <label
                            class="text-slate-600 dark:text-slate-300 text-sm font-medium leading-5"
                        >
                            Select card
                        </label>
                        <div class="w-full">
                            <UserCardSearch
                                v-model:input="manualCard.chosenCard"
                                :model-value="manualCard.chosenCard"
                                :current-card="manualChosenCard"
                                @cardSelected="$emit('cardSelected', $event)"
                                @clear-current-card="$emit('clearSelectedCard')"
                                placeholder="Type card name or issuing bank"
                                label=""
                                class="w-full"
                                :validation-error="
                                    fieldErrors.card_selection
                                "
                            />
                        </div>
                    </div>

                    <!-- Card Not Listed Section -->
                    <div
                        class="flex flex-col items-start self-stretch mt-2"
                    >
                        <button
                            type="button"
                            @click="$emit('openMissingCard')"
                            class="text-slate-800 dark:text-slate-100 text-sm font-medium leading-5 hover:text-slate-600 dark:hover:text-slate-300 transition-colors cursor-pointer"
                        >
                            Card not listed?
                        </button>
                    </div>

                    <!-- Last Digits Section -->
                    <div
                        class="flex flex-col items-start self-stretch mt-6"
                    >
                        <label
                            class="text-slate-600 dark:text-slate-300 text-sm font-medium leading-5 mb-2"
                        >
                            Last digits of card
                            <span class="text-slate-500 dark:text-slate-400 font-normal">
                                (optional)
                            </span>
                        </label>
                        <div class="w-full md:w-1/2 mb-6">
                            <input
                                class="w-full px-3 py-2 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-md text-sm text-slate-800 dark:text-slate-100 placeholder:text-slate-500 dark:placeholder-slate-500 focus:outline-none focus:ring-0 focus:border-slate-800 dark:focus:border-slate-500 hover:border-slate-800 dark:hover:border-slate-500 transition-colors"
                                v-model="manualCard.last_four"
                                type="text"
                                placeholder="Enter last 4 or 5 digits"
                                maxlength="5"
                                v-maska
                                data-maska="#####"
                            />
                        </div>
                    </div>
                </div>
                <transition>
                    <div
                        v-if="manualCard.card_not_found"
                        class="pt-2 mt-8 w-full"
                    >
                        <div
                            class="rounded-lg bg-gray-100 dark:bg-slate-700 px-6 py-4 w-full md:w-3/4 mx-auto mb-10 shadow border dark:border-slate-600 flex flex-wrap"
                        >
                            <div class="mb-3 w-full">
                                <h3
                                    class="text-xl mb-5 capitalize text-left"
                                >
                                    Missing Card Request
                                </h3>
                            </div>
                            <div
                                class="mb-1 md:mb-3 w-full md:w-1/2 md:pr-2"
                            >
                                <UiInput
                                    id="card_not_found_bank"
                                    name="card_not_found_bank"
                                    label="Issuing Bank"
                                    :options="{ hideLabel: true }"
                                >
                                    <template #input>
                                        <span
                                            class="block w-full mb-2 text-sm text-left"
                                        >
                                            Issuing Bank
                                        </span>
                                        <input
                                            type="text"
                                            class="form-input w-full mb-2"
                                            v-model="
                                                manualCard.card_not_found_bank
                                            "
                                            placeholder="e.g. American Express"
                                        />
                                    </template>
                                </UiInput>
                            </div>
                            <div
                                class="mb-1 md:mb-3 w-full md:w-1/2 md:pl-2"
                            >
                                <UiInput
                                    id="card_not_found_name"
                                    name="card_not_found_name"
                                    label="Credit Card Name"
                                    :options="{ hideLabel: true }"
                                >
                                    <template #input>
                                        <span
                                            class="block w-full mb-2 text-sm text-left"
                                        >
                                            Credit Card Name
                                        </span>
                                        <input
                                            type="text"
                                            class="form-input w-full"
                                            v-model="
                                                manualCard.card_not_found_name
                                            "
                                            placeholder="e.g. Chase Freedom Unlimited®"
                                        />
                                    </template>
                                </UiInput>
                            </div>
                        </div>
                        <div class="prose dark:prose-invert max-w-prose mx-auto text-sm">
                            <p>
                                Please enter the official credit card name
                                (e.g., Chase Freedom Unlimited®).
                                <strong>
                                    Do not enter your personal name or card
                                    number.
                                </strong>
                                Please be as accurate as possible in
                                reference to the card name so that we can
                                ensure we have the correct card.
                            </p>
                            <p>
                                We'll then work to get this card added to
                                our system, and you'll be notified once it's
                                been added.
                            </p>
                        </div>
                    </div>
                </transition>
                <!-- Error Messages -->
                <div class="w-full" v-show="errors.length > 0">
                    <Errors :errors="errors" />
                </div>

                <!-- Mobile bottom bar -->
                <div
                    class="md:hidden fixed bottom-0 left-0 right-0 bg-white dark:bg-slate-800 px-4 pt-3 pb-[max(12px,env(safe-area-inset-bottom))] z-[1000]"
                >
                    <div class="grid grid-cols-2 gap-2">
                        <button
                            type="button"
                            @click.prevent="$emit('gotoStep', 2)"
                            class="w-full py-3 rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-100 text-sm font-medium leading-5 hover:bg-slate-50 dark:hover:bg-slate-700"
                            id="hs-wallet-manual-card-entry-back-mobile"
                        >
                            Back
                        </button>
                        <button
                            type="button"
                            v-if="!manualCard.card_not_found"
                            @click.prevent="$emit('gotoManualStep', 2)"
                            class="w-full py-3 rounded-md bg-slate-800 dark:bg-slate-600 text-white text-sm font-medium leading-5 hover:bg-slate-700 dark:hover:bg-slate-500"
                            id="hs-wallet-manual-card-entry-next-mobile"
                        >
                            Next
                        </button>
                        <ActionButton
                            v-if="manualCard.card_not_found"
                            @clicked="$emit('addCard')"
                            label="Submit Request"
                            :loading="addCardBtnAction"
                            classes="w-full py-3 rounded-md bg-slate-800 dark:bg-slate-600 text-white text-sm font-medium leading-5 hover:bg-slate-700 dark:hover:bg-slate-500"
                        />
                    </div>
                </div>

                <!-- Footer Container (desktop only) -->
                <div
                    class="hidden md:flex justify-between items-center self-stretch relative bg-white dark:bg-slate-800 flex-shrink-0 mt-auto w-full"
                >
                    <!-- Back Button -->
                    <button
                        type="button"
                        @click.prevent="$emit('gotoStep', 2)"
                        class="flex py-2.5 px-4 items-start gap-2 rounded border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 shadow-[0_1px_2px_0_rgba(0,0,0,0.05)] text-slate-800 dark:text-slate-100 text-sm font-medium leading-5 hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors"
                        id="hs-wallet-manual-card-entry-back"
                    >
                        Back
                    </button>

                    <!-- Progress Indicators - Centered -->
                    <div
                        class="flex items-center gap-2 absolute left-1/2 -translate-x-1/2"
                    >
                        <!-- Step 1 - Active -->
                        <div class="w-6 h-1 rounded-sm bg-slate-800 dark:bg-slate-100"></div>
                        <!-- Step 2 - Inactive -->
                        <div class="w-6 h-1 rounded-sm bg-slate-300 dark:bg-slate-600"></div>
                        <!-- Step 3 - Inactive -->
                        <div class="w-6 h-1 rounded-sm bg-slate-300 dark:bg-slate-600"></div>
                    </div>

                    <!-- Right Buttons Container -->
                    <div class="flex items-start gap-2">
                        <!-- Cancel Button -->
                        <button
                            type="button"
                            @click.prevent="$emit('close')"
                            class="flex py-2.5 px-4 items-start gap-2 rounded border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 shadow-[0_1px_2px_0_rgba(0,0,0,0.05)] text-slate-800 dark:text-slate-100 text-sm font-medium leading-5 hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors"
                        >
                            Cancel
                        </button>

                        <!-- Next Button -->
                        <button
                            type="button"
                            v-if="!manualCard.card_not_found"
                            @click.prevent="$emit('gotoManualStep', 2)"
                            class="flex py-2.5 px-4 justify-center items-center gap-2 rounded bg-slate-800 dark:bg-slate-600 shadow-[0_1px_2px_0_rgba(0,0,0,0.05)] text-white text-sm font-medium leading-5 hover:bg-slate-700 dark:hover:bg-slate-500 transition-colors"
                            id="hs-wallet-manual-card-entry-next"
                        >
                            Next
                        </button>

                        <!-- Submit Request Button -->
                        <ActionButton
                            v-if="manualCard.card_not_found"
                            @clicked="$emit('addCard')"
                            label="Submit Request"
                            :loading="addCardBtnAction"
                            classes="flex py-2.5 px-4 justify-center items-center gap-2 rounded bg-slate-800 dark:bg-slate-600 shadow-[0_1px_2px_0_rgba(0,0,0,0.05)] text-white text-sm font-medium leading-5 hover:bg-slate-700 dark:hover:bg-slate-500 transition-colors"
                        />
                    </div>
                </div>
            </div>
        </template>
    </div>
</template>

<script setup>
import Icon from '@/components/ui/icon/icon.vue';
import UserCardSearch from '@/components/ui/user-card-search.vue';
import UiInput from '@/components/ui/inputs/ui-input.vue';
import ActionButton from '@/components/action-button.vue';
import Errors from '@/components/errors.vue';
import { vMaska } from 'maska/vue';
import { computed } from 'vue';

const props = defineProps({
    manualCard: { type: Object, required: true },
    manualChosenCard: { type: Object, default: null },
    fieldErrors: { type: Object, required: true },
    errors: { type: Array, required: true },
    addCardBtnAction: { type: Boolean, default: false },
    currentStep: { type: Number, required: true },
    user: { type: Object, default: null },
    hasMultipleUsers: { type: Boolean, default: false },
});

defineEmits([
    'close',
    'closeMissingCard',
    'gotoStep',
    'gotoManualStep',
    'addCard',
    'cardSelected',
    'clearSelectedCard',
    'openMissingCard',
    'submitMissingCard',
    'clearFieldError',
    'update:currentStep',
]);

const cdnUrl = computed(() => window.cdnUrl);
</script>
